import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import "./SubscriptionSuccess.css";

const MAX_RETRIES = 3;
const POLL_INTERVAL = 2000;

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();

  const [status, setStatus] = useState("loading"); // loading | success | pending
  const [planName, setPlanName] = useState("");

  const isOnboarding =
    localStorage.getItem("viewsday_onboarding_in_progress") === "true";

  // Capture the plan at mount time so refreshUser() doesn't invalidate our check
  const initialPlanRef = useRef(user?.plan || "free");

  useEffect(() => {
    let cancelled = false;

    const poll = async (retries) => {
      try {
        const sub = await api.getSubscription();

        if (cancelled) return;

        if (sub.plan && sub.plan !== initialPlanRef.current) {
          // Plan changed — upgrade confirmed
          await refreshUser();
          setPlanName(capitalize(sub.plan));
          setStatus("success");
          return;
        }

        if (retries < MAX_RETRIES) {
          setTimeout(() => poll(retries + 1), POLL_INTERVAL);
        } else {
          // Exhausted retries — refresh user anyway in case webhook fires later
          await refreshUser();
          setStatus("pending");
        }
      } catch {
        if (cancelled) return;
        if (retries < MAX_RETRIES) {
          setTimeout(() => poll(retries + 1), POLL_INTERVAL);
        } else {
          setStatus("pending");
        }
      }
    };

    poll(0);

    return () => { cancelled = true; };
  }, [refreshUser]);

  const handleNavigate = () => {
    if (isOnboarding) {
      localStorage.removeItem("viewsday_onboarding_in_progress");
      navigate("/onboarding?step=2");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="ss-page">
      <div className="ss-card">
        {status === "loading" && (
          <div className="ss-loading">
            <span className="ss-spinner" />
            <p className="ss-desc">{t("subscription.verifying")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="ss-content">
            <div className="ss-icon ss-icon-success">
              <span className="ss-checkmark">&#10003;</span>
            </div>
            <h1 className="ss-title">
              {t("subscription.successTitle", { plan: planName })}
            </h1>
            <p className="ss-desc">{t("subscription.successDesc")}</p>
            <button className="btn-primary btn-md" onClick={handleNavigate}>
              {isOnboarding
                ? t("subscription.continueSetup")
                : t("subscription.goToDashboard")}
            </button>
          </div>
        )}

        {status === "pending" && (
          <div className="ss-content">
            <div className="ss-icon ss-icon-info">
              <span className="ss-info-char">i</span>
            </div>
            <p className="ss-desc ss-desc-pending">
              {t("subscription.successPending")}
            </p>
            <button className="btn-primary btn-md" onClick={handleNavigate}>
              {t("subscription.goToDashboard")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
